import { PendingAction, useCatalystFlow } from '@/contexts/CatalystFlowContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, X, AlertTriangle, Calendar, ListTodo, TrendingUp, Mail, HelpCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ActionConfirmationProps {
  action: PendingAction;
  onConfirm: () => void;
  onReject: () => void;
}

export function ActionConfirmation({ action, onConfirm, onReject }: ActionConfirmationProps) {
  const { sendMessage } = useCatalystFlow();
  const isDestructive = action.type === 'archive' || action.type === 'delete';
  const isResolved = action.confirmed !== null;

  const getIcon = () => {
    switch (action.entityType) {
      case 'task':
        return <ListTodo className="h-4 w-4" />;
      case 'meeting':
      case 'follow_up':
        return <Calendar className="h-4 w-4" />;
      case 'lead':
        return <TrendingUp className="h-4 w-4" />;
      case 'invoice_reminder':
        return <Mail className="h-4 w-4" />;
      default:
        return <ListTodo className="h-4 w-4" />;
    }
  };

  const handleModify = () => {
    sendMessage("I'd like to modify this plan. What alternatives do you suggest?");
    onReject();
  };

  const handleWhyThese = () => {
    sendMessage("Why did you recommend these specific actions? What's the reasoning?");
  };

  if (isResolved) {
    return (
      <Card className={cn(
        'p-3 flex items-center gap-2 text-sm',
        action.confirmed 
          ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900' 
          : 'bg-muted/50'
      )}>
        {action.confirmed ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <X className="h-4 w-4 text-muted-foreground" />
        )}
        <span className={cn(
          action.confirmed ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground line-through'
        )}>
          {action.description}
        </span>
      </Card>
    );
  }

  return (
    <Card className={cn(
      'p-3 space-y-2',
      isDestructive && 'border-orange-200 bg-orange-50/50 dark:border-orange-900 dark:bg-orange-950/20'
    )}>
      <div className="flex items-start gap-2">
        <div className={cn(
          'h-6 w-6 rounded flex items-center justify-center shrink-0 mt-0.5',
          isDestructive ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/50' : 'bg-primary/10 text-primary'
        )}>
          {isDestructive ? <AlertTriangle className="h-3.5 w-3.5" /> : getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{action.description}</p>
          {isDestructive && (
            <p className="text-xs text-muted-foreground mt-0.5">
              This action requires your confirmation
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2 justify-end flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleWhyThese}
          className="h-7 text-xs text-muted-foreground"
        >
          <HelpCircle className="h-3 w-3 mr-1" />
          Why?
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleModify}
          className="h-7 text-xs text-muted-foreground"
        >
          Modify
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onReject}
          className="h-7 text-xs"
        >
          <Clock className="h-3 w-3 mr-1" />
          Not Now
        </Button>
        <Button
          variant={isDestructive ? "destructive" : "default"}
          size="sm"
          onClick={onConfirm}
          className="h-7 text-xs"
        >
          <Check className="h-3 w-3 mr-1" />
          Execute
        </Button>
      </div>
    </Card>
  );
}
